import { ApiResult, HttpClient } from "../http.js";
import {
  AnnouncementFeedDto,
  AnnouncementPlatform,
  AnnouncementReadResultDto,
  ProductNewsPreferenceDto,
} from "../types/announcements.js";

export interface AnnouncementFeedQuery {
  platform: AnnouncementPlatform;
  /** Native app version, e.g. "1.2.0". Sent for ios/android. */
  version?: string;
  /** Web bundle build date, e.g. "2026-10-29". Sent for web. */
  build?: string;
}

export interface MarkAnnouncementsReadPayload {
  /** Omitted or empty marks every item the caller's feed shows. */
  ids?: string[];
}

function toParams(query: AnnouncementFeedQuery): Record<string, string> {
  const params: Record<string, string> = { platform: query.platform };
  if (query.version !== undefined) params.version = query.version;
  if (query.build !== undefined) params.build = query.build;
  return params;
}

export class AnnouncementsApi {
  constructor(private readonly http: HttpClient) {}

  /** The caller's "Nyt i Mesterplan" feed, gated by platform and app version / web build. */
  feed(query: AnnouncementFeedQuery): Promise<ApiResult<AnnouncementFeedDto>> {
    return this.http.get<AnnouncementFeedDto>("/api/announcements/me", { query: toParams(query) });
  }

  /**
   * Marks announcements read for the caller. No ids = everything the feed shows.
   * The same gate as `feed` must be sent so the server only touches notes this client can see.
   */
  markRead(query: AnnouncementFeedQuery, ids?: string[]): Promise<ApiResult<AnnouncementReadResultDto>> {
    const body: MarkAnnouncementsReadPayload = ids && ids.length > 0 ? { ids } : {};
    return this.http.post<AnnouncementReadResultDto>("/api/announcements/me/read", {
      query: toParams(query),
      body,
    });
  }

  getPreferences(): Promise<ApiResult<ProductNewsPreferenceDto>> {
    return this.http.get<ProductNewsPreferenceDto>("/api/announcements/me/preferences");
  }

  setPreferences(showAutomatically: boolean): Promise<ApiResult<ProductNewsPreferenceDto>> {
    return this.http.put<ProductNewsPreferenceDto>("/api/announcements/me/preferences", {
      body: { showAutomatically },
    });
  }
}
